/*
  One definition of the shape/edge metrics, shared by the single-analysis Shape
  panel and the Compare Analyses Shape tab.

  Kept here rather than in either page because the two showed the same numbers
  under different labels otherwise, which reads as two different measurements.

  Keys match the camelCase fields produced when morphology/results.json is
  normalised (see MORPH_FIELD_MAP), not the snake_case in the file itself.
*/

export const MORPH_METRICS = [
  { key: 'circularity',        label: 'Circularity',         hint: '1.0 = perfect circle' },
  { key: 'irregularity',       label: 'Irregularity',        hint: '1 − solidity; lobes and bays' },
  { key: 'convexity',          label: 'Edge convexity',      hint: 'hull perimeter ÷ actual; a crenulated rim lowers it' },
  { key: 'eccentricity',       label: 'Eccentricity',        hint: '0 = round, 1 = elongated' },
  { key: 'solidity',           label: 'Solidity',            hint: 'area ÷ convex hull area' },
  { key: 'extent',             label: 'Extent',              hint: 'area ÷ bounding-box area' },
  { key: 'equivalentDiameter', label: 'Equivalent diameter', hint: 'diameter of a circle of equal area (px)' },
  { key: 'perimeter',          label: 'Perimeter',           hint: 'Crofton estimate (px)' },
  { key: 'textureContrast',    label: 'Texture contrast',    hint: 'GLCM; surface roughness' },
  { key: 'textureHomogeneity', label: 'Texture homogeneity', hint: 'GLCM; surface uniformity' },
  { key: 'edgeDensity',        label: 'Edge density',        hint: 'detected edge fraction' },
  { key: 'intensityMean',      label: 'Mean intensity',      hint: 'grey level inside the region' },
];

/** snake_case field in results.json -> camelCase key used by the charts. */
export const MORPH_FIELD_MAP = {
  area_px: 'area',
  perimeter_px: 'perimeter',
  circularity: 'circularity',
  irregularity: 'irregularity',
  convexity: 'convexity',
  solidity: 'solidity',
  eccentricity: 'eccentricity',
  equivalent_diameter_px: 'equivalentDiameter',
  extent: 'extent',
  contour_count: 'contourCount',
  texture_contrast: 'textureContrast',
  texture_homogeneity: 'textureHomogeneity',
  texture_energy: 'textureEnergy',
  texture_correlation: 'textureCorrelation',
  intensity_mean: 'intensityMean',
  intensity_std: 'intensityStd',
  edge_density: 'edgeDensity',
};

/**
 * morphology/results.json -> [{object, frame, isoTime, date, ...metrics}].
 *
 * Ratio metrics are null on frames whose mask is empty. That means "undefined",
 * not zero, so nulls are preserved and the chart leaves a gap instead of
 * drawing a plunge to the axis.
 */
export function parseMorphology(parsed, tsMap = {}) {
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((obj) =>
    (obj.frames || []).map((f) => {
      const iso = tsMap[String(f.frame_idx)] || '';
      const row = {
        object: String(obj.obj_id),
        frame: f.frame_idx,
        isoTime: iso,
        date: iso ? new Date(iso) : null,
      };
      Object.entries(MORPH_FIELD_MAP).forEach(([src, dst]) => {
        row[dst] = f[src] ?? null;
      });
      return row;
    })
  );
}

/** Metrics with at least one real value in these rows — the rest aren't offered. */
export function availableMetrics(rows) {
  if (!rows || !rows.length) return [];
  return MORPH_METRICS.filter((m) =>
    rows.some((r) => r[m.key] !== null && r[m.key] !== undefined));
}
